import React, { ReactElement, useState, useRef, useLayoutEffect, useEffect, useMemo } from 'react';
import classNames from 'classnames/bind';
import { connect } from 'react-redux';
import Button from '../../../refresh/atoms/button';
import Image from '../../../refresh/atoms/ImageView';
import styles from './index.pcss';
import { signInCheck } from '../CheckUserStatus';
import { setAdditionalInfo } from '../../../actions/shared/productsData';
import {
  getPriceRevealSaleDuration,
  getSaleStartEndText,
  getDeviceBasedFlag
} from './priceRevealUtil';
import { DURING_SALE, PRE_BUZZ } from './messages';
import { setPrimaryHeight } from '../../../actions/webMetaData';
import { gtmEventPush } from '../../../modules/ga/GTMdatalayer';
import { sanitize } from '../../desktop/header/SearchBar';


interface PriceRevealProps {
  productsAdditionalInfo: {
    isPriceReveal: boolean;
    priceRevealMetaInfo: {
      startTime: string;
      endTime: string;
    };
  };
  deviceType: string;
  setAdditionalInfo: (p: { isPriceReveal: boolean }) => void;
  setPrimaryHeight: (p: number) => void;
  flags: {
    priceReveal: string;
  };
  store: string;
  sis: boolean;
  luxe: boolean;
  dynamicAppInstallBanners: string;
}

interface ReduxStateReturnProps {
  productsAdditionalInfo: {
    isPriceReveal: boolean;
    priceRevealMetaInfo: {
      startTime: string;
      endTime: string;
    };
  };
  flags: {
    priceReveal: string;
  };
  store: string;
  sis: boolean;
}

interface ReduxStateProps {
  productsAdditionalInfo: {
    isPriceReveal: boolean;
    priceRevealMetaInfo: {
      startTime: string;
      endTime: string;
    };
  };
  flags: {
    priceReveal: string;
  };
  metaData: {
    name: string;
    sis: boolean;
  };
}

const mapStateToProps = (state: ReduxStateProps): ReduxStateReturnProps => ({
  productsAdditionalInfo: state.productsAdditionalInfo,
  flags: state.flags,
  store: state.metaData.name || 'AJIO',
  sis: state.metaData.sis
});

const mapDispatchToProps = { setAdditionalInfo, setPrimaryHeight };

const connector = connect(mapStateToProps, mapDispatchToProps);

const cx = classNames.bind(styles);

const PriceRevealPLPBanner = (props: PriceRevealProps): ReactElement | null => {
  const {
    productsAdditionalInfo: {
      isPriceReveal = false,
      priceRevealMetaInfo = {},
      dealStartTime = '',
      dealEndTime = ''
    } = {},
    setAdditionalInfo,
    setPrimaryHeight,
    deviceType = 'msite',
    flags = {},
    store = 'AJIO',
    sis = false,
    luxe,
    dynamicAppInstallBanners
  } = props;

  const isAjioStore = String(store).toLowerCase() === 'ajio' || String(store).toLowerCase() === 'rilfnl';

  if (dealStartTime !== '' || dealEndTime !== '') return null;
  const priceRevealConfigData = useMemo<{
    bbsTextButton: string;
    luxeBbsTextButton: string;
  }>(() => {
    return getDeviceBasedFlag(deviceType, flags) as {
      bbsTextButton: string;
      bannerImgSrc: any;
      priceImgSrc: any;
      luxePriceImgSrc: any;
      wishlistText: string;
      bbsDescription: string;
      luxeWishlistText: string;
      luxeBBSDescription: string;
      luxeBbsTextButton: string;
    };
  }, [deviceType, flags]);

  const BBSButtonText = luxe
    ? priceRevealConfigData?.luxeBbsTextButton?.toUpperCase()
    : priceRevealConfigData?.bbsTextButton;

  const priceRevealState = getPriceRevealSaleDuration(
    flags,
    deviceType,
    priceRevealMetaInfo,
    store
  );
  const stickyTop = isAjioStore && dynamicAppInstallBanners === 'ON' ? 110 : 60;
  const [isSticky, setSticky] = useState(false);
  const ref = useRef(null);
  const stickyTopRef = useRef(stickyTop);
  stickyTopRef.current = stickyTop;

  const scrollCallback = useRef((): void => {
    if (ref && ref.current) {
      setSticky(ref.current.getBoundingClientRect().top <= stickyTopRef.current + 5);
    }
  });

  useLayoutEffect(() => {
    window.addEventListener('scroll', scrollCallback.current);

    return () => {
      window.removeEventListener('scroll', scrollCallback.current);
    };
  }, []);

  useEffect(() => {
    const username = signInCheck();
    let defaultPriceReveal = false;
    if (priceRevealState === DURING_SALE) {
      defaultPriceReveal = true;
    }
    if (priceRevealState === PRE_BUZZ) {
      if (username && localStorage.getItem('priceReveal_v2')) {
        defaultPriceReveal = true;
      }
    }
    setAdditionalInfo({
      isPriceReveal: defaultPriceReveal
    });
    if (priceRevealState) {
      setPrimaryHeight(140);
    }
  }, []);

  const handleClick = (): void => {
    const username = signInCheck();
    gtmEventPush({
      event: 'bbs_widget_interaction',
      event_category: 'widget interaction',
      event_action: 'bbs widget',
      event_label: 'show bbs price',
      screen_type: deviceType,
      screen_name: 'product listing page',
      user_status: username ? 'logged in' : 'not logged In',
      login_type: username ? 'logged in' : 'not logged In',
      bbs_widget_state: localStorage?.getItem('priceReveal_v2')
        ? 'sale price revealed'
        : 'sale price not revealed'
    });
    localStorage.setItem('priceReveal_v2', true);
    if (!username) {
      const {
        location: { pathname, search }
      } = window;
      window.location.href = sanitize(
        `/login?referrer=${pathname}${search}&login_source=plp - price reveal widget`
      );
      return;
    }
    setAdditionalInfo({ isPriceReveal: true });
  };

  const getSubText = (state: string | null, priceFlag: boolean): string => {
    if (state === DURING_SALE) {
      if (luxe) {
        return priceRevealConfigData.bbsBannerText;
      }
      return priceRevealConfigData.wishlistText;
    }
    if (luxe) {
      return priceFlag
        ? priceRevealConfigData.luxeWishlistText
        : priceRevealConfigData.luxeBBSDescription;
    }
    return priceFlag ? priceRevealConfigData.wishlistText : priceRevealConfigData.bbsDescription;
  };

  if (!priceRevealState) {
    return <div />;
  }

  if (deviceType === 'desktop') {
    return (
      <div
        className={cx(luxe ? 'luxePriceRevealDesktop' : 'priceRevealDesktop', { storeLayout: sis })}
      >
        <div className={cx(luxe ? 'luxePriceRevealDesktopImage' : '')}>
          <Image
            src={
              luxe ? priceRevealConfigData?.luxeBannerImgSrc?.image : priceRevealConfigData?.bannerImgSrc?.image
            }
            className={cx(luxe ? 'luxeBannerImageDesktop' : '')}
            alt={
              luxe ? priceRevealConfigData?.luxeBannerImgSrc?.altText : priceRevealConfigData?.bannerImgSrc?.altText ||"price-reveal"}
          />
        </div>
        <div
          className={cx(
            'boldText',
            'paddingBottom',
            luxe ? 'luxePriceRevealBannerTextColor' : 'colorDarkBlue'
          )}
        >
          {getSaleStartEndText(priceRevealMetaInfo, priceRevealState, flags, deviceType, store)}
        </div>
        <span className={luxe ? cx('vertical-line', 'luxe') : cx('vertical-line')} aria-hidden="true" tabIndex={-1}/>
        {luxe ? (
          <div
            dangerouslySetInnerHTML={{ __html: getSubText(priceRevealState, isPriceReveal) }}
            className={cx(
              'smallText',
              'paddingBottom',
              luxe ? cx('luxePriceRevealSmallText') : undefined
            )}
          />
        ) : (
          <div className={cx('paddingBottom')}>{getSubText(priceRevealState, isPriceReveal)}</div>
        )}
        {!isPriceReveal &&
          priceRevealState === PRE_BUZZ &&
          (luxe ? (
            <div className={cx('saleButton')}>
              <Button
                fontSize="sm"
                btnLabel={BBSButtonText}
                variant="primary"
                customClass="luxePriceRevealShowButton"
                onClick={handleClick}
              />
            </div>
          ) : (
            <div>
              <button type="button" className="btn-gold" onClick={handleClick}>
                {priceRevealConfigData.bbsTextButton}
              </button>
            </div>
          ))}
      </div>
    );
  }
  const wrapperStyle: React.CSSProperties = {
    ...(luxe ? { marginBottom: '39px' } : {}),
    position: 'sticky',
    top: `${stickyTop}px`,
    zIndex: 199,
    backfaceVisibility: 'hidden',
    WebkitBackfaceVisibility: 'hidden',
  };

  return (
    <div
      style={wrapperStyle}
      className={cx(luxe ? 'luxePriceRevealBanner' : 'priceRevealPLPBanner')}
      id="priceRevealPLPBanner"
      ref={ref}
    >
      <div className={cx(luxe ? 'luxeExpandBanner' : 'expandBanner')} style={isSticky ? { display: 'none' } : undefined}>
          <div className={cx('logoContainer')}>
            <Image
              src={
                luxe ? priceRevealConfigData?.luxeBannerImgSrc?.image : priceRevealConfigData?.bannerImgSrc?.image
              }
              alt={
                luxe ? priceRevealConfigData?.luxeBannerImgSrc?.altText : priceRevealConfigData?.bannerImgSrc?.altText
              }
            />
          </div>
          <div className={cx(luxe ? 'luxeTextContainer' : 'textContainer')}>
            <div
              className={cx(
                'boldText',
                'paddingBottom',
                luxe ? 'luxePriceRevealBannerTextColor' : 'colorDarkBlue'
              )}
            >
              <strong>{getSaleStartEndText(priceRevealMetaInfo, priceRevealState, flags, deviceType, store)}</strong>
            </div>
            {luxe ? (
              <div
                dangerouslySetInnerHTML={{ __html: getSubText(priceRevealState, isPriceReveal) }}
                className={cx(
                  'smallText',
                  'paddingBottom',
                  luxe
                    ? priceRevealState === PRE_BUZZ
                      ? 'luxePriceRevealSmallText'
                      : 'luxePriceRevealVerySmallText'
                    : undefined
                )}
              />
            ) : (
              <div className={cx('paddingBottom')}>
                {getSubText(priceRevealState, isPriceReveal)}
              </div>
            )}
            {!isPriceReveal && priceRevealState === PRE_BUZZ && (
              <div className={cx('saleButton')}>
                <Button
                  size="sm"
                  btnLabel={BBSButtonText}
                  variant="primary"
                  customClass={luxe ? cx('luxePriceRevealShowButton') : undefined}
                  onClick={handleClick}
                />
              </div>
            )}
          </div>
        </div>
        <div className={cx(luxe ? 'luxeCollapsedBanner' : 'collapsedBanner')} style={isSticky ? undefined : { display: 'none' }}>
          <div className={cx('logoContainer', 'text-center')}>
            <Image
              src={
                luxe ? priceRevealConfigData?.luxePriceImgSrc?.image : priceRevealConfigData?.priceImgSrc?.image
              }
              alt={luxe ? priceRevealConfigData?.luxePriceImgSrc?.altText : priceRevealConfigData?.priceImgSrc?.altText ||"price-reveal" }
            />
          </div>
          <div className={cx('collapsedTextContainer')}>
            <div
              className={cx('boldText', luxe ? 'luxePriceRevealBannerTextColor' : 'colorDarkBlue')}
            >
              {getSaleStartEndText(priceRevealMetaInfo, priceRevealState, flags, deviceType, store)}
            </div>
          </div>
        </div>
    </div>
  );
};

export default connector(PriceRevealPLPBanner);
